import styled, { keyframes, css } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Background-position sweep used by the skeleton placeholders below.
   200% size + -200% → 200% travel keeps the highlight passing fully
   through the bar without flashing back. */
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

export const PageWrap = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 28px 0 64px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  flex: 1;
  font-family: var(--font-family-primary);
  color: var(--color-text-body);

  @media (max-width: 768px) {
    padding: 18px 0 48px;
    gap: 16px;
  }
`;

/* ─── Profile Row (avatar + name + email + sign out) ───────────────────── */

export const ProfileRow = styled.section`
  display: flex;
  align-items: center;
  gap: 18px;
  animation: ${fadeUp} 320ms var(--ease-snappy) both;
`;

export const Avatar = styled.div`
  width: 76px;
  height: 76px;
  flex-shrink: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 25%,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(186, 220, 255, 0.7) 50%,
    rgba(118, 182, 244, 0.55) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    0 8px 18px rgba(var(--color-brand-500-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 480px) {
    width: 64px;
    height: 64px;
  }
`;

export const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const UsernameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Username = styled.h1`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--color-text-strong);

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

export const EditUsernameButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 0;
  transition:
    transform 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
  }

  &:hover {
    transform: translateY(-1px);
    color: var(--color-text-strong);
  }
`;

export const Email = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-muted);
`;

export const VerifiedDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: linear-gradient(180deg, #34d399 0%, #059669 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 9px;
  font-weight: 800;

  &::after {
    content: "✓";
  }
`;

export const SignOutButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition:
    transform 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
  }

  &:hover {
    transform: translateY(-1px);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ─── Balance Card ─────────────────────────────────────────────────────── */

export const BalanceCard = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 22px 24px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
  animation: ${fadeUp} 360ms var(--ease-snappy) both;
  /* overflow: hidden clips the heavy-blur ::before disc to the card
     border-radius, the same trick NudeAI uses on their balance panel. */
  overflow: hidden;
  isolation: isolate;

  /* Soft brand-blue "lamp" that sits behind the DropIcon. A heavily
     blurred circular block (256×256, blur-3xl ≈ 56px) positioned just
     outside the top-left corner so its core lands behind the icon and
     the outer halo bleeds smoothly across the card. The card's
     overflow: hidden clips the disc to the rounded panel — the shape
     of the glow follows the card edge naturally. */
  &::before {
    content: "";
    position: absolute;
    left: -80px;
    top: -80px;
    width: 256px;
    height: 256px;
    border-radius: 50%;
    background: rgba(var(--color-brand-500-rgb), 0.18);
    filter: blur(56px);
    pointer-events: none;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 14px 28px rgba(0, 0, 0, 0.55),
      0 6px 14px rgba(0, 0, 0, 0.32);
  }

  [data-theme="dark"] &::before {
    background: rgba(var(--color-brand-400-rgb), 0.22);
  }

  > * {
    position: relative;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    gap: 16px;
    padding: 18px 18px 20px;
  }
`;

/* Naked icon slot — no circle, no halo background. Just gives the
   DropIcon a fixed slot in the row and colours it via `currentColor`.
   The ambient blue glow you see on the card comes from BalanceCard's
   ::before / ::after gradients, anchored at this icon's position;
   that's how the reference (NudeAI) renders its flame mark and it
   reads cleaner than a hard-edged badge. */
export const BalanceIconWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand-500);

  [data-theme="dark"] & {
    color: var(--color-brand-400);
  }

  /* Soft drop-shadow on the SVG so the drop sits with a hint of depth
     even though it's now drawn on the bare card surface. The glow that
     surrounds the icon comes from the BalanceCard radial gradient. */
  svg {
    display: block;
    filter: drop-shadow(0 2px 4px rgba(var(--color-brand-700-rgb), 0.35))
      drop-shadow(0 0 12px rgba(var(--color-brand-400-rgb), 0.55));
  }

  @media (max-width: 600px) {
    align-self: center;
  }
`;

export const BalanceMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  @media (max-width: 600px) {
    align-items: center;
  }
`;

/* Drop icon + numeric value inline so they read as a single unit
   ("💧 1,855") on phone instead of stacking the droplet above the
   number. */
export const BalanceValueRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const BalanceLabel = styled.span`
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

export const BalanceValue = styled.span`
  font-family: var(--font-family-primary);
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--color-text-strong);
  font-variant-numeric: tabular-nums;
`;

export const TopUpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  height: 48px;
  padding: 0 28px;
  border-radius: 999px;
  border: 1px solid transparent;
  border: none;
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.38),
    0 4px 10px rgba(var(--color-brand-600-rgb), 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border: none;
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.55),
      0 3px 8px rgba(0, 0, 0, 0.32),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.32);
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
    [data-theme="dark"] &:hover {
      box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.5),
        0 3px 8px rgba(0, 0, 0, 0.3),
        inset 0 -3px 6px rgba(0, 0, 0, 0.22);
    }
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.46),
      0 4px 12px rgba(var(--color-brand-600-rgb), 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.65),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

/* ─── Recent activities ────────────────────────────────────────────────── */

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeUp} 420ms var(--ease-snappy) both;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.015em;
  color: var(--color-text-strong);
`;

export const ActivityTable = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1.2fr;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
  isolation: isolate;

  /* Phone: small horizontal margin so the card's drop shadow has room
     to render inside MainContainer (which clips horizontal overflow).
     Without this, the shadow gets sliced flat at the screen edges. */
  @media (max-width: 640px) {
    margin: 0 4px;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 22px;
    background: radial-gradient(
      110% 60% at 50% 0%,
      rgba(var(--color-brand-300-rgb), 0.22) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 14px 28px rgba(0, 0, 0, 0.55),
      0 6px 14px rgba(0, 0, 0, 0.32);
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Phone: convert the flat 4-column table into a 2x2 stacked card per
     record so all four fields stay visible. THs are hidden; each record's
     four TDs lay out as Type/Amount on top and Status/Date below. */
  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;
    column-gap: 0;
    padding: 4px 0;
  }
`;

export const TH = styled.div`
  padding: 14px 18px;
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  border-bottom: 1px solid var(--color-line);

  &:last-child {
    text-align: right;
  }

  /* Mobile drops the column headers entirely — each record stands alone. */
  @media (max-width: 640px) {
    display: none;
  }
`;

export const TD = styled.div`
  padding: 14px 18px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-strong);
  border-bottom: 1px solid var(--color-line);

  /* Date column — every 4th cell. Muted + right-aligned. Was previously
     :last-child, which only matched the very last cell of the grid (so
     only the final row's date looked different from the rest). */
  &:nth-of-type(4n) {
    text-align: right;
    color: var(--color-text-muted);
    font-weight: 500;
  }

  ${({ $row }) =>
    $row === "last" &&
    `
      border-bottom: none;
    `}

  /* Phone — collapse 4 TDs of a record into a 2x2 card.
     1: Type (large)        2: Amount (large, colored)
     3: Status (pill)       4: Date (small, muted)
     Generous vertical gap between rows for breathing room; soft full-width
     gradient separator drawn between records via the bottom-left cell's
     ::after pseudo (so it spans the whole row regardless of column-gap). */
  @media (max-width: 640px) {
    padding: 4px 18px;
    border-bottom: none;
    font-size: 14px;
    line-height: 1.25;

    /* Top row of each record (Type left, Amount right) */
    &:nth-of-type(4n + 1) {
      grid-column: 1;
      padding-top: 16px;
      padding-bottom: 4px;
      font-size: 15px;
      font-weight: 700;
      color: var(--color-text-strong);
    }
    &:nth-of-type(4n + 2) {
      grid-column: 2;
      padding-top: 16px;
      padding-bottom: 4px;
      text-align: right;
      font-size: 15px;
      font-weight: 800;
    }
    /* Bottom row (Status left → also draws the separator beneath both
       cells of this record; Date right) */
    &:nth-of-type(4n + 3) {
      grid-column: 1;
      padding-top: 4px;
      padding-bottom: 18px;
      position: relative;
    }
    &:nth-of-type(4n + 3)::after {
      content: "";
      position: absolute;
      left: 18px;
      right: 18px;
      bottom: 0;
      height: 1px;
      width: calc(200% - 36px);
      background: linear-gradient(
        90deg,
        rgba(var(--color-brand-300-rgb), 0) 0%,
        rgba(var(--color-brand-300-rgb), 0.32) 30%,
        rgba(var(--color-brand-300-rgb), 0.32) 70%,
        rgba(var(--color-brand-300-rgb), 0) 100%
      );
      pointer-events: none;
    }
    &:nth-of-type(4n) {
      grid-column: 2;
      padding-top: 4px;
      padding-bottom: 18px;
      text-align: right;
      color: var(--color-text-muted);
      font-size: 12px;
      font-weight: 500;
    }
  }

  /* Final record drops the separator. */
  ${({ $row }) =>
    $row === "last" &&
    `
      @media (max-width: 640px) {
        &:nth-of-type(4n + 3)::after {
          display: none;
        }
      }
    `}
`;

export const Amount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ $positive }) =>
    $positive ? "#22c55e" : "var(--color-text-strong)"};
  font-weight: 700;
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ $variant }) =>
    $variant === "completed"
      ? "rgba(34, 197, 94, 0.18)"
      : "rgba(var(--color-brand-300-rgb), 0.22)"};
  color: ${({ $variant }) =>
    $variant === "completed" ? "#16a34a" : "var(--color-brand-700)"};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "completed"
        ? "rgba(34, 197, 94, 0.32)"
        : "rgba(var(--color-brand-400-rgb), 0.3)"};

  [data-theme="dark"] & {
    color: ${({ $variant }) =>
      $variant === "completed" ? "#4ade80" : "var(--color-text-strong)"};
  }
`;

export const EmptyHistory = styled.div`
  padding: 26px;
  text-align: center;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: var(--color-text-muted);
  grid-column: 1 / -1;
`;

/* ─── Skeleton placeholders ────────────────────────────────────────────── */

/* Single shimmer bar used inside an empty TD while the activity feed is
   still loading. Width is configurable so each row's four cells can
   each pick a width that roughly matches the live data they'll be
   replaced by — gives the loading state visual rhythm rather than four
   identical strips.

   `$variant="pill"` swaps the rounded-square shape for a fully-rounded
   pill so the Status column's skeleton matches the StatusPill sizing
   of real rows; without it that column read as a flat bar instead of
   a chip and the layout shifted on hydration. */
export const SkeletonBar = styled.span`
  display: inline-block;
  width: ${({ $w }) => $w || "70%"};
  max-width: 100%;
  height: ${({ $variant }) => ($variant === "pill" ? "20px" : "14px")};
  border-radius: ${({ $variant }) => ($variant === "pill" ? "999px" : "6px")};
  /* Light-theme shimmer uses semi-transparent black so the bars read
     against the white-ish page surface. The previous surface-2/3 stops
     blended into the page on tinted light tokens and the skeleton was
     barely visible. */
  background: linear-gradient(
    90deg,
    rgba(15, 23, 42, 0.07) 0%,
    rgba(15, 23, 42, 0.13) 50%,
    rgba(15, 23, 42, 0.07) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  vertical-align: middle;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);

  [data-theme="dark"] & {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.045) 0%,
      rgba(255, 255, 255, 0.11) 50%,
      rgba(255, 255, 255, 0.045) 100%
    );
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
  }

  ${({ $variant }) =>
    $variant === "pill" &&
    css`
      width: ${({ $w }) => $w || "78px"};
      height: 20px;
    `}

  /* Mobile: live rows scale type up to ~15px / pill ~20px; bump the
     bars to roughly match so the skeleton row reads at the same visual
     weight as a real row when it streams in. */
  @media (max-width: 640px) {
    height: ${({ $variant }) => ($variant === "pill" ? "20px" : "13px")};
  }
`;

/* ─── Danger zone ──────────────────────────────────────────────────────── */

/* Account-deletion section, collapsed by default. Renders as a solid
   surface-1 card so it sits in the page rhythm with the other dashboard
   sections (BalanceCard, activity Section) instead of fading into the
   background. The destructive action lives behind a chevron toggle —
   no red ink on the page until the user opens the panel. */
export const DangerCard = styled.section`
  position: relative;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  overflow: hidden;
  animation: ${fadeUp} 480ms var(--ease-snappy) both;

  [data-theme="dark"] & {
    background: var(--color-surface-2);
    border-color: rgba(255, 255, 255, 0.06);
  }
`;

/* Clickable header row — title on the left, animated chevron on the
   right. Hover-only background tint signals the row is interactive
   without competing with neighbouring sections. */
export const DangerHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 160ms var(--ease-snappy);

  &:hover {
    background: var(--color-surface-2);
  }

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  @media (max-width: 520px) {
    padding: 12px 14px;
  }
`;

export const DangerTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  color: var(--color-text-strong);
`;

export const DangerChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: var(--color-text-muted);
  transition: transform 220ms var(--ease-snappy);
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

/* Collapsible region — grid-row trick gives a smooth height animation
   from 0 to content-height without measuring JS-side. Same pattern as
   the TopUpModal plan summary expansion. */
export const DangerExpansion = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    grid-template-rows 280ms var(--ease-snappy),
    opacity 220ms var(--ease-snappy);

  > div {
    overflow: hidden;
  }
`;

export const DangerExpansionInner = styled.div`
  padding: 0 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid var(--color-line);
  padding-top: 12px;

  [data-theme="dark"] & {
    border-top-color: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 520px) {
    padding: 10px 14px 14px;
  }
`;

export const DangerText = styled.p`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  color: var(--color-text-muted);
`;

/* Coloured delete button — solid red gradient pill so the destructive
   action is unambiguous once the user has chosen to expand the panel.
   Stays out of sight while the section is collapsed. */
export const DeleteButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(207, 47, 76, 0.55);
  background: linear-gradient(180deg, #e64561 0%, #cf2f4c 100%);
  color: #ffffff;
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(102, 14, 30, 0.3);
  cursor: pointer;
  box-shadow:
    0 6px 14px -4px rgba(230, 69, 97, 0.35),
    0 2px 4px rgba(230, 69, 97, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 10px 18px -4px rgba(230, 69, 97, 0.45),
      0 3px 6px rgba(230, 69, 97, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
  }
`;

/* ─── Legacy exports — kept so imports from old SubscriptionPage build don't
   break. None are used by the new dashboard. ─── */

const noop = styled.div``;
const noopButton = styled.button``;
const noopSpan = styled.span``;
const noopInput = styled.input``;
const noopP = styled.p``;
const noopUl = styled.ul``;
const noopLi = styled.li``;

export const Container = noop;
export const BackButton = noopButton;
export const Title = noop;
export const TitleRow = noop;
export const PlanBalanceCard = noop;
export const PlanHero = noop;
export const PlanHeroTitleRow = noop;
export const PlanHeroLabel = noopSpan;
export const PlanValue = noopSpan;
export const CreditsInfo = noop;
export const CreditsLabel = noopSpan;
export const CreditsValue = noopSpan;
export const CreditHistoryToggle = noopButton;
export const CreditHistoryToggleLabel = noopSpan;
export const CreditHistoryToggleText = noopSpan;
export const CreditHistoryCollapse = noop;
export const Table = noop;
export const TableRow = noop;
export const TableHeader = noop;
export const TableCell = noop;
export const TableFooter = noop;
export const FooterText = noopSpan;
export const Pagination = noop;
export const PaginationButton = noopButton;
export const PaginationCurrent = noopSpan;
export const PricingSection = noop;
export const BillingToggle = noop;
export const PlansGrid = noop;
export const PlanCard = noop;
export const PopularBadge = noopSpan;
export const PlanName = noop;
export const PlanDescription = noopP;
export const PriceSection = noop;
export const OriginalPrice = noopSpan;
export const CurrentPrice = noopSpan;
export const PricePeriod = noopSpan;
export const FeaturesList = noopUl;
export const Feature = noopLi;
export const FAQSection = noop;
export const FAQList = noop;
export const FAQItem = noop;
export const FAQQuestion = noop;
export const FAQAnswer = noop;
export const TableContainer = noop;
export const Tooltip = noopSpan;
export const TimeAgoCell = noop;
export const TableLoading = noop;
export const HeaderContainer = noop;
export const BillingSegment = noopButton;
export const SaleBadge = noopSpan;
export const RenewButton = noopButton;
export const CreditsContainer = noop;
export const FeatureLabel = noopSpan;
export const FeatureValue = noopSpan;
export const FeatureCheck = noopSpan;
export const FeatureHeroValue = noopSpan;
export const FeatureHeroLabel = noopSpan;
export const FeatureHero = noop;
export const ThinText = noopSpan;
export const PaymentButtonsContainer = noop;
export const PaymentButton = noopButton;
export const RechargeSection = noop;
export const RechargeSectionTitle = noop;
export const RechargeSectionSubtitle = noopP;
export const RechargeGrid = noop;
export const RechargeCard = noop;
export const RechargeCardHeader = noop;
export const RechargeCardName = noop;
export const RechargeCardRate = noopSpan;
export const RechargeCardHero = noop;
export const RechargeCardHeroLabel = noopSpan;
export const RechargeCardDivider = noop;
export const RechargeCardPriceRow = noop;
export const RechargeCardPriceLabel = noopSpan;
export const RechargeCardPriceValue = noopSpan;
export const RechargeCustomRow = noop;
export const RechargeCustomPrefix = noopSpan;
export const RechargeCustomInput = noopInput;
export const PlansHeading = noop;
export const PlansHeadingTitle = noop;
export const PlansHeadingSubtitle = noopP;
